import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INVENTORY_COLLECTIONS,
  REGISTRY_COLLECTIONS,
  type OrgRecord,
  type Paginated,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../../lib/api.js';
import { useOrg } from '../../../lib/auth.js';
import { Button, Dialog, Field, Select, useToast } from '../../../components/ui.js';

/**
 * Import a point-in-time snapshot of an inventory/registry into this response
 * as a markdown table. The import happens server-side as a new version and is
 * recorded in the audit log as a dynamic import (response.snapshot_imported).
 */
export function SnapshotImport({
  responseId,
  unsaved,
  onImported,
}: {
  responseId: string;
  /** The editor has unsaved changes the import would not include. */
  unsaved?: boolean;
  onImported: () => void;
}) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [collection, setCollection] = useState('');
  const [mode, setMode] = useState<'append' | 'replace'>('append');

  const records = useQuery({
    queryKey: ['records', org.orgId, 'all-active'],
    queryFn: () =>
      apiFetch<Paginated<OrgRecord>>(`/orgs/${org.orgId}/records`, {
        query: { status: 'active', limit: 200 },
      }),
    enabled: open,
  });

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of records.data?.items ?? []) {
      map.set(r.collection, (map.get(r.collection) ?? 0) + 1);
    }
    return map;
  }, [records.data]);

  const importSnapshot = useMutation({
    mutationFn: () =>
      apiFetch(`/responses/${responseId}/record-snapshot`, {
        method: 'POST',
        body: { collection, mode },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['response', responseId] });
      void queryClient.invalidateQueries({ queryKey: ['versions', responseId] });
      toast('Snapshot imported as a new version.', 'success');
      setOpen(false);
      setCollection('');
      onImported();
    },
    onError: (err) =>
      toast(
        err instanceof ApiRequestError ? err.message : 'Could not import the snapshot',
        'error',
      ),
  });

  const label = (key: string, name: string) => `${name} (${counts.get(key) ?? 0} active)`;

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Insert records snapshot
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Insert records snapshot">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Copies your live inventory or registry into this response as a table — a point-in-time
          snapshot. It is saved as a new version and recorded in the audit log as a dynamic import.
        </p>
        {unsaved && (
          <p style={{ color: 'var(--color-warning)' }}>
            You have unsaved changes in the editor. The snapshot applies to the last saved version —
            save your changes first if you want to keep them.
          </p>
        )}
        <Field label="Source">
          {(props) => (
            <Select {...props} value={collection} onChange={(e) => setCollection(e.target.value)}>
              <option value="">Select a source…</option>
              <optgroup label="Inventory">
                {INVENTORY_COLLECTIONS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {label(d.key, d.name)}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Registries">
                {REGISTRY_COLLECTIONS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {label(d.key, d.name)}
                  </option>
                ))}
              </optgroup>
            </Select>
          )}
        </Field>
        <Field label="Placement">
          {(props) => (
            <Select
              {...props}
              value={mode}
              onChange={(e) => setMode(e.target.value as 'append' | 'replace')}
            >
              <option value="append">Append below the current content</option>
              <option value="replace">Replace the current content</option>
            </Select>
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={!collection}
            loading={importSnapshot.isPending}
            onClick={() => importSnapshot.mutate()}
          >
            Import snapshot
          </Button>
        </div>
      </Dialog>
    </>
  );
}
